import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Observable, finalize, map, of } from 'rxjs';
import { BreadcrumbService, ModalService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { ApDocumentQuickViewModalComponent } from '../../shared/modals/ap-document-quick-view-modal/ap-document-quick-view-modal.component';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { reportInvalidFormFields } from '../../../utilities/form-error-reporter.util';
import { ApDocument, ApDocumentStatus } from '../../../models/ap-document/ap-document';
import { Currency } from '../../../models/bank-account/bank-account';
import { Business, BusinessEntityType } from '../../../models/business';
import { BusinessProxyService } from '../../../proxies/business.proxy.service';
import { WithholdingDocumentProxyService } from '../../../proxies/withholding-document.proxy.service';
import { WithholdingTaxProxyService } from '../../../proxies/withholding-tax.proxy.service';
import { WITHHOLDING_COUNTRY_CODE, WithholdingResidency, WithholdingTaxCode } from '../../../models/withholding/withholding';
import {
  WithholdingClaimedLine,
  WithholdingDocument,
  WithholdingDocumentSource,
  WithholdingSaveCommand,
} from '../../../models/withholding-document/withholding-document';
import {
  WithholdingFormGroup,
  WithholdingLineDecision,
  WithholdingLineFormGroup,
  WithholdingSetupFormGroup,
  WithholdingSourceFormGroup,
} from '../../../models/withholding-document/withholding-document-form';
import { EnumService } from '../../../services/enum.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { RATE_DECIMALS } from '../../../utilities/settlement-rate.util';

interface ResidencyOption {
  label: string;
  value: WithholdingResidency;
}

/**
 * Constancia de Retención page (#4454/#5122).
 *
 * <p>The officer opens a certificate on the two things that define it — a PROVIDER and ONE withholding code
 * — then gathers the AP documents it covers. How many it may hold depends on the provider's domicile, frozen
 * onto the certificate when it was created: a locally domiciled provider gets one document per certificate,
 * as always; a foreign provider's certificate can gather several.</p>
 *
 * <p>Per line the decision is only whether it is withheld or exempt, because the code is no longer a per-line
 * choice. Removing a line is a different act with the opposite meaning — see {@link removeLine}.</p>
 *
 * <p>Amounts are shown in each document's own currency AND in Lempira, which is what the retention is owed
 * in whatever the invoice was billed in. Every figure here is a preview; the server recomputes on save.</p>
 */
@Component({
  selector: 'app-ap-withholding-details',
  templateUrl: './ap-withholding-details.component.html',
  styleUrl: './ap-withholding-details.component.scss',
})
export class ApWithholdingDetailsComponent implements OnInit {
  /** Rate precision for the rate inputs — the API's (CurrencyConverter.RateDecimals, #5359). */
  protected readonly rateDecimals = RATE_DECIMALS;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly businessService = inject(BusinessProxyService);
  private readonly withholdingDocumentService = inject(WithholdingDocumentProxyService);
  private readonly withholdingService = inject(WithholdingTaxProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly modalService = inject(ModalService);
  private readonly enumService = inject(EnumService);
  private readonly translate = inject(TranslateService);
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly ApDocumentStatus = ApDocumentStatus;
  /** The retention is owed to SAR in Lempira whatever the invoices were billed in. */
  readonly localCurrency = Currency.HNL;

  isLoading = signal(0);
  isNew = signal(true);
  currentId = signal<string | null>(null);
  status = signal<number>(ApDocumentStatus.Draft);
  headerTitle = signal<string>(this.t('Withholding_New', 'New Withholding Certificate'));
  /** Self-issued Constancia fiscal number (No. Retención) + CAI — drawn on post (#4487). */
  constanciaNumber = signal<string>('');
  constanciaCai = signal<string>('');

  /** The loaded certificate, for the facts the form does not hold (domicile, rate, totals as posted). */
  private readonly certificate = signal<WithholdingDocument | null>(null);

  // ---- Accounting / GL impact (#5024 #5031) ----
  readonly journalEntryReference = signal<string | null>(null);
  readonly journalEntryDocumentNumber = signal<string | null>(null);
  readonly showAccounting = computed(() => !!this.journalEntryReference());
  readonly journalEntryReferences = computed(() => {
    const reference = this.journalEntryReference();
    return reference ? [reference] : [];
  });
  readonly hasConstanciaNumber = computed(() => !!this.constanciaNumber());
  /** Guards the print button while the PDF is being rendered (#5945). */
  readonly isPrinting = signal(false);

  // ── Setup: provider + the one tax ───────────────────────────────────────────
  usableCodes = signal<WithholdingTaxCode[]>([]);
  withholdingResidency = signal<WithholdingResidency>(WithholdingResidency.Resident);
  /** Fixed, non-editable: we only withhold Honduran taxes, whatever the provider's domicile. */
  readonly withholdingCountry = WITHHOLDING_COUNTRY_CODE;

  readonly setupForm = new FormGroup<WithholdingSetupFormGroup>({
    provider: new FormControl<Business | null>(null, [Validators.required]),
    withholdingCode: new FormControl<WithholdingTaxCode | null>(null, [Validators.required]),
  });

  readonly setupFieldLabels: Record<keyof WithholdingSetupFormGroup, string> = {
    provider: 'Withholding_Details_Form_Field_Provider',
    withholdingCode: 'Withholding_Details_Form_Field_WithholdingCode',
  };

  // ── The certificate itself ──────────────────────────────────────────────────
  readonly form = new FormGroup<WithholdingFormGroup>({
    // Read-only, and always today on a new certificate: a Constancia is dated the day it is issued, and that
    // is the date SAR reads it by — not something the officer chooses. Disabled rather than removed from the
    // form, because every submit goes through `getRawValue()`, which still carries disabled controls.
    documentDate: new FormControl<Date | null>({ value: new Date(), disabled: true }, [Validators.required]),
    postingDate: new FormControl<Date | null>(new Date(), [Validators.required]),
    notes: new FormControl<string | null>(null),
    sources: new FormArray<FormGroup<WithholdingSourceFormGroup>>([]),
  });

  readonly fieldLabels: Record<keyof WithholdingFormGroup, string> = {
    documentDate: 'Withholding_Details_Form_Field_DocumentDate',
    postingDate: 'Withholding_Details_Form_Field_PostingDate',
    notes: 'Withholding_Details_Form_Field_Notes',
    sources: 'Withholding_Details_Form_Field_Sources',
  };

  /**
   * Every total below is derived from the form, so reading this makes them recompute as the officer works.
   * The value itself is unused — the controls are read directly, which keeps the arithmetic in one place
   * instead of mirroring the whole nested array into a parallel signal.
   */
  private readonly formChanges = toSignal(this.form.valueChanges);

  // ── AP document picker ──────────────────────────────────────────────────────
  /** Cleared after each add so the box never sits showing a document already on the certificate. */
  documentPickerValue = signal<ApDocument | null>(null);
  readonly documentSearchFieldOptions: { label: string; value: 'number' | 'fiscalNumber' }[] = [
    { label: 'Document #', value: 'number' },
    { label: 'Provider Document #', value: 'fiscalNumber' },
  ];
  documentSearchField = signal<'number' | 'fiscalNumber'>('number');

  /**
   * The three outcomes, in escalating order of consequence: withhold, exempt (covered, nothing withheld),
   * remove (off the certificate, AP document stays blocked). Labels carry the meaning because the icons
   * alone cannot — exempt and remove look alike and do opposite things.
   */
  readonly decisionOptions: { label: string; value: WithholdingLineDecision }[] = [
    { label: this.t('Withholding_Decision_Withhold', 'Withhold'), value: 'withhold' },
    { label: this.t('Withholding_Decision_Exempt', 'Exempt'), value: 'exempt' },
    { label: this.t('Withholding_Decision_Remove', 'Remove'), value: 'remove' },
  ];

  readonly residencyOptions: ResidencyOption[] = [
    { label: 'Resident / domiciled', value: WithholdingResidency.Resident },
    { label: 'Non-resident', value: WithholdingResidency.NonResident },
    { label: 'Any', value: WithholdingResidency.Any },
  ];
  readonly usableCodeOptions = computed(() =>
    this.usableCodes().map((c) => ({ label: `${c.code} — ${this.localeHelper.getLocalizedValue(c.name)} (${c.rate}%)`, value: c })),
  );

  private currencyLabels: Record<number, string> = {};
  private statusLabels: Record<number, string> = {};

  get sources(): FormArray<FormGroup<WithholdingSourceFormGroup>> {
    return this.form.controls.sources;
  }

  // ── State the template branches on ──────────────────────────────────────────
  readonly isDraft = computed(() => this.status() === ApDocumentStatus.Draft);
  readonly isPersisted = computed(() => !this.isNew() && !!this.currentId());
  /** Before the draft exists the page is the setup form and nothing else. */
  readonly isSetupPhase = computed(() => !this.isPersisted());

  /** Frozen at creation — re-domiciling the provider later must not reshape an issued certificate. */
  readonly isLocalProvider = computed(() => this.certificate()?.isLocalProvider ?? true);
  readonly providerCountryCode = computed(() => this.certificate()?.providerCountryCode ?? null);
  readonly certificateProviderName = computed(() => this.certificate()?.providerName ?? '');
  readonly withholdingRate = computed(
    () => this.certificate()?.withholdingRate ?? this.setupForm.controls.withholdingCode.value?.rate ?? 0,
  );
  readonly withholdingCodeText = computed(
    () => this.certificate()?.withholdingCode ?? this.setupForm.controls.withholdingCode.value?.code ?? '',
  );

  readonly sourceCount = computed(() => {
    this.formChanges();
    return this.sources.length;
  });

  /** A local provider's certificate holds exactly one document; a foreign provider's may hold several. */
  readonly canAddSource = computed(() => this.isDraft() && this.isPersisted() && (!this.isLocalProvider() || this.sourceCount() === 0));

  /** Explains the shape in words, so the officer is never surprised by a refusal. */
  readonly shapeHint = computed(() =>
    this.isLocalProvider()
      ? this.t('Withholding_Shape_Local', 'This provider is domiciled locally, so this certificate covers exactly one AP document.')
      : this.t(
          'Withholding_Shape_Foreign',
          'This provider is domiciled abroad, so this certificate can cover several AP documents under the same tax.',
        ),
  );

  constructor() {
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
    this.statusLabels = this.enumService.getEnum('ApDocumentStatus').values as Record<number, string>;
  }

  ngOnInit(): void {
    const id = this.route.snapshot.queryParamMap.get('id');
    if (id && id !== 'new') {
      this.isNew.set(false);
      this.currentId.set(id);
      this.loadCertificate(id);
    } else {
      this.breadcrumbService.setCurrent('Common_New');
    }
  }

  // ── helpers ─────────────────────────────────────────────────────────────────
  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  /**
   * Downloads the printed Comprobante de Retención (#5945). The certificate is rendered server-side from
   * what the document froze on post, so the same file comes back however many times it is reprinted.
   */
  printCertificate(): void {
    const id = this.currentId();
    if (!id || this.isPrinting()) return;

    this.isPrinting.set(true);
    this.withholdingDocumentService
      .getCertificate(id)
      .pipe(finalize(() => this.isPrinting.set(false)))
      // Failures are toasted by the shared errorInterceptor, which reads the API's own message out of the blob.
      .subscribe((blob) => this.triggerDownload(blob, `constancia-retencion_${this.constanciaNumber()}.pdf`));
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  readonly localCurrencyText = computed(() => this.currencyLabel(this.localCurrency));

  statusLabelText(): string {
    return this.statusLabels[this.status()] ?? '';
  }

  statusSeverity(): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    switch (this.status()) {
      case ApDocumentStatus.Posted:
        return 'success';
      case ApDocumentStatus.Draft:
        return 'secondary';
      default:
        return 'info';
    }
  }

  residencyLabel(value: WithholdingResidency | null | undefined): string {
    return this.residencyOptions.find((o) => o.value === value)?.label ?? '—';
  }

  getSourceGroup(row: unknown): FormGroup<WithholdingSourceFormGroup> {
    return row as FormGroup<WithholdingSourceFormGroup>;
  }

  getLineGroup(row: unknown): FormGroup<WithholdingLineFormGroup> {
    return row as FormGroup<WithholdingLineFormGroup>;
  }

  /** True when the source is already in Lempira, which converts at 1 and has no rate to correct. */
  sourceIsLocal(source: FormGroup<WithholdingSourceFormGroup>): boolean {
    return source.controls.currency.value === this.localCurrency;
  }

  // ── Setup: provider + code ──────────────────────────────────────────────────
  public fetchProviders = (query: string): Observable<Business[]> =>
    this.businessService
      .search({ name: query, code: query, businessTypes: [BusinessEntityType.Provider] }, 1, 8, undefined, undefined, true)
      .pipe(map((response) => response.data?.items ?? []));

  onProviderSelected(provider: Business): void {
    // The residency scopes which catalog codes apply; the jurisdiction is ours and never theirs.
    this.withholdingResidency.set(provider?.providerDetails?.withholdingResidency ?? WithholdingResidency.Resident);
    this.setupForm.controls.withholdingCode.setValue(null, { emitEvent: false });
    this.loadUsableCodes();
  }

  onProviderCleared(): void {
    this.setupForm.controls.withholdingCode.setValue(null, { emitEvent: false });
    this.usableCodes.set([]);
  }

  private loadUsableCodes(): void {
    this.withholdingService.getUsableCodes(this.withholdingCountry, null, this.withholdingResidency()).subscribe({
      next: (res) => this.usableCodes.set(res.success && res.data ? res.data : []),
      error: () => this.usableCodes.set([]),
    });
  }

  /**
   * Opens the draft. The server resolves and FREEZES the provider's domicile onto it, which is what decides
   * whether more than one AP document may be attached — so the answer cannot drift afterwards.
   */
  createCertificate(): void {
    if (this.setupForm.invalid) {
      this.setupForm.markAllAsTouched();
      reportInvalidFormFields(this.setupForm, this.setupFieldLabels, this.translate);
      return;
    }
    const provider = this.setupForm.controls.provider.value;
    const code = this.setupForm.controls.withholdingCode.value;
    if (!provider?.id || !code?.code) return;

    const dates = this.form.getRawValue();
    this.startLoading();
    this.withholdingDocumentService
      .create({
        providerReference: provider.id,
        withholdingCode: code.code,
        documentDate: dates.documentDate ? dates.documentDate.toISOString() : null,
        postingDate: dates.postingDate ? dates.postingDate.toISOString() : null,
        notes: dates.notes ?? null,
      })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const created = response.data;
          if (created?.id) {
            this.currentId.set(created.id);
            this.isNew.set(false);
            this.router.navigate([], { queryParams: { id: created.id }, replaceUrl: true });
            this.patchFromCertificate(created);
          }
        },
      });
  }

  // ── AP documents ────────────────────────────────────────────────────────────
  public fetchDocuments = (query: string): Observable<ApDocument[]> => {
    const providerId = this.certificate()?.providerReference;
    if (!providerId) return of([]);
    // Eligibility lives on the API (#5122): this provider's posted Invoice/Receipt, still requiring
    // withholding, with at least one service line no posted certificate has claimed. This screen sends only
    // what the officer typed, so the picker can never offer a document the add then refuses.
    const command =
      this.documentSearchField() === 'fiscalNumber'
        ? { status: ApDocumentStatus.Posted, fiscalNumber: query }
        : { status: ApDocumentStatus.Posted, number: query };

    return this.withholdingDocumentService
      .searchEligibleDocuments(providerId, command, 1, 10)
      .pipe(map((response) => response.data?.items ?? []));
  };

  onDocumentSelected(document: ApDocument): void {
    const id = this.currentId();
    if (!id || !document?.id) return;
    this.startLoading();
    this.withholdingDocumentService
      .addSources(id, { documentReferences: [document.id] })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          // Freshly seeded lines arrive undecided — the officer has not looked at them yet.
          if (response.data) this.patchFromCertificate(response.data, document.id);
          this.documentPickerValue.set(null);
          if (document.id) this.loadClaimedLines(id, document.id);
        },
      });
  }

  removeSource(source: FormGroup<WithholdingSourceFormGroup>): void {
    const id = this.currentId();
    const documentReference = source.controls.documentReference.value;
    if (!id || !documentReference) return;

    this.confirmationService.confirm({
      header: this.t('Withholding_RemoveSource_Header', 'Remove AP document'),
      message: this.t(
        'Withholding_RemoveSource_Message',
        'Its lines are released and can be claimed by another certificate. Until one is, the document stays blocked for payment. Continue?',
      ),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.startLoading();
        this.withholdingDocumentService
          .removeSource(id, documentReference)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: (response) => {
              if (response.data) this.patchFromCertificate(response.data);
            },
          });
      },
    });
  }

  /**
   * Restores a source's lines from its AP document — the undo for a removal that was already saved.
   *
   * <p>Before a save, "remove" is only a decision on the form and switching it back is free. Once saved the
   * line is genuinely off the certificate, and the officer's only remedy used to be detaching the whole
   * document and re-attaching it, losing every other decision on it. Decisions survive a reset; lines a
   * posted certificate has since claimed do not come back, and {@link claimedLinesFor} says why.</p>
   */
  resetSourceLines(source: FormGroup<WithholdingSourceFormGroup>): void {
    const id = this.currentId();
    const documentReference = source.controls.documentReference.value;
    if (!id || !documentReference) return;

    this.startLoading();
    this.withholdingDocumentService
      .resetSourceLines(id, documentReference)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          // Restored lines arrive undecided, exactly like newly seeded ones — a line the officer removed and
          // brought back is a line they have to decide about again.
          if (response.data) this.patchFromCertificate(response.data, documentReference);
          this.loadClaimedLines(id, documentReference);
        },
      });
  }

  /** Lines another POSTED certificate holds, keyed by AP document — shown as locked rows. */
  private readonly claimedLines = signal<Record<string, WithholdingClaimedLine[]>>({});

  claimedLinesFor(source: FormGroup<WithholdingSourceFormGroup>): WithholdingClaimedLine[] {
    return this.claimedLines()[source.controls.documentReference.value] ?? [];
  }

  private loadClaimedLines(id: string, documentReference: string): void {
    this.withholdingDocumentService.getSourceClaimedLines(id, documentReference).subscribe({
      next: (response) => this.claimedLines.update((map) => ({ ...map, [documentReference]: response.data ?? [] })),
      // Purely explanatory — a failed lookup costs the officer an explanation, never an action.
      error: () => this.claimedLines.update((map) => ({ ...map, [documentReference]: [] })),
    });
  }

  /**
   * Read-only quick view of the AP document a source came from (#4789).
   *
   * <p>A modal rather than navigation on purpose: this page holds unsaved per-line decisions, and routing
   * away to look up an invoice number would discard them.</p>
   */
  openDocumentQuickView(source: FormGroup<WithholdingSourceFormGroup>): void {
    const documentReference = source.controls.documentReference.value;
    if (!documentReference) return;
    // Wide: the amounts block and the line table both carry a functional column.
    this.modalService
      .open(ApDocumentQuickViewModalComponent, { modalClass: 'modal_middle' }, { documentId: documentReference })
      .subscribe();
  }

  /** Every line on a source marked for removal — the reset button is only meaningful once one exists. */
  sourceHasRemovals(source: FormGroup<WithholdingSourceFormGroup>): boolean {
    return source.controls.lines.controls.some((l) => l.controls.decision.value === 'remove');
  }

  // ── load / patch ────────────────────────────────────────────────────────────
  private loadCertificate(id: string): void {
    this.startLoading();
    this.withholdingDocumentService
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) this.patchFromCertificate(response.data);
        },
      });
  }

  private patchFromCertificate(certificate: WithholdingDocument, seededDocumentReference?: string | null): void {
    this.certificate.set(certificate);
    this.status.set(certificate.status);
    this.headerTitle.set(`${this.t('Withholding', 'Withholding Certificate')} ${certificate.number ?? ''}`.trim());
    this.breadcrumbService.setCurrent(certificate.number);
    this.constanciaNumber.set(certificate.certificateNumber ?? '');
    this.constanciaCai.set(certificate.cai ?? '');
    this.journalEntryReference.set(certificate.journalEntryReference ?? null);
    this.journalEntryDocumentNumber.set(certificate.journalEntryDocumentNumber ?? null);
    this.withholdingResidency.set(certificate.withholdingResidency ?? WithholdingResidency.Resident);

    this.form.patchValue(
      {
        documentDate: certificate.documentDate ? new Date(certificate.documentDate) : null,
        postingDate: certificate.postingDate ? new Date(certificate.postingDate) : null,
        notes: certificate.notes ?? null,
      },
      { emitEvent: false },
    );

    // The setup fields are read-only once the certificate exists — they are frozen server-side too.
    this.setupForm.controls.provider.setValue(
      { id: certificate.providerReference, code: certificate.providerCode, name: certificate.providerName } as Business,
      { emitEvent: false },
    );

    this.rebuildSources(certificate.sources ?? [], seededDocumentReference);

    // Which lines other posted certificates hold, so a missing line reads as "claimed by WHD-…" rather than
    // as an unexplained gap. One call per source; a certificate carries a handful at most.
    const certificateId = certificate.id;
    if (certificateId)
      for (const source of certificate.sources ?? [])
        if (source.document?.reference) this.loadClaimedLines(certificateId, source.document.reference);

    if (certificate.status !== ApDocumentStatus.Draft) this.form.disable({ emitEvent: false });
    else this.form.enable({ emitEvent: false });
    // enable() reopens EVERY control, so the issue date has to be locked back down after it.
    this.form.controls.documentDate.disable({ emitEvent: false });
  }

  /**
   * Rebuilds the source blocks from the server's copy.
   *
   * <p><paramref name="seededDocumentReference"/> names a document whose lines have just been seeded — by
   * attaching it or by resetting it — and whose lines therefore arrive UNDECIDED. Every other source is
   * being re-read after a save, so its lines carry decisions the officer already made.</p>
   */
  private rebuildSources(sources: WithholdingDocumentSource[], seededDocumentReference?: string | null): void {
    this.sources.clear({ emitEvent: false });
    for (const source of sources) {
      const decided = !seededDocumentReference || source.document?.reference !== seededDocumentReference;
      const lines = new FormArray<FormGroup<WithholdingLineFormGroup>>([]);
      for (const line of source.lines ?? []) {
        lines.push(
          new FormGroup<WithholdingLineFormGroup>({
            sourceLineNumber: new FormControl<number>(line.sourceLineNumber ?? line.lineNumber, { nonNullable: true }),
            description: new FormControl<string>(line.description ?? '', { nonNullable: true }),
            baseAmount: new FormControl<number>(line.baseAmount ?? 0, { nonNullable: true }),
            localBaseAmount: new FormControl<number>(line.localBaseAmount ?? 0, { nonNullable: true }),
            // A stored line has been decided; a freshly seeded one has not. `isExempt` is the only thing the
            // server records, so "withhold" is what a non-exempt STORED line means — but a line that has
            // never been saved arrives undecided and must stay that way.
            decision: new FormControl<WithholdingLineDecision | null>(decided ? (line.isExempt ? 'exempt' : 'withhold') : null),
          }),
        );
      }

      this.sources.push(
        new FormGroup<WithholdingSourceFormGroup>({
          documentReference: new FormControl<string>(source.document?.reference ?? '', { nonNullable: true }),
          documentNumber: new FormControl<string>(source.document?.number ?? '', { nonNullable: true }),
          fiscalNumber: new FormControl<string>(source.document?.fiscalNumber ?? '', { nonNullable: true }),
          currency: new FormControl<number>(source.currency, { nonNullable: true }),
          localRate: new FormControl<number>(source.localRate ?? 1, { nonNullable: true }),
          rateDate: new FormControl<Date | null>(source.rateDate ? new Date(source.rateDate) : null),
          lines,
        }),
        { emitEvent: false },
      );
    }
    this.form.updateValueAndValidity();
  }

  // ── amounts (preview only; the server recomputes on save) ────────────────────
  /** A line's retained amount in the source's own currency: base × the certificate's rate%. */
  lineAmount(line: FormGroup<WithholdingLineFormGroup>): number {
    // Only a line decided "withhold" contributes. Exempt withholds nothing; removed is not on the
    // certificate at all; undecided has no answer yet and must not be counted as one.
    if (line.controls.decision.value !== 'withhold') return 0;
    return this.round(line.controls.baseAmount.value * (this.withholdingRate() / 100));
  }

  /** The same amount in Lempira, at the source's own rate — what reaches the Constancia. */
  lineLocalAmount(source: FormGroup<WithholdingSourceFormGroup>, line: FormGroup<WithholdingLineFormGroup>): number {
    return this.round(this.lineAmount(line) * (source.controls.localRate.value || 0));
  }

  sourceRetained(source: FormGroup<WithholdingSourceFormGroup>): number {
    return this.round(source.controls.lines.controls.reduce((acc, l) => acc + this.lineAmount(l), 0));
  }

  sourceRetainedLocal(source: FormGroup<WithholdingSourceFormGroup>): number {
    return this.round(source.controls.lines.controls.reduce((acc, l) => acc + this.lineLocalAmount(source, l), 0));
  }

  /** Lempira is the one figure that stays meaningful across sources billed in different currencies. */
  readonly totalBaseLocal = computed(() => {
    this.formChanges();
    return this.round(
      this.sources.controls.reduce(
        (acc, s) =>
          acc +
          s.controls.lines.controls
            // A line marked for removal is leaving the certificate, so it is not part of its base either.
            .filter((l) => l.controls.decision.value !== 'remove')
            .reduce((sum, l) => sum + l.controls.localBaseAmount.value, 0),
        0,
      ),
    );
  });

  readonly totalWithheldLocal = computed(() => {
    this.formChanges();
    return this.round(this.sources.controls.reduce((acc, s) => acc + this.sourceRetainedLocal(s), 0));
  });

  readonly totalNetLocal = computed(() => this.round(this.totalBaseLocal() - this.totalWithheldLocal()));

  readonly lineCount = computed(() => {
    this.formChanges();
    return this.sources.controls.reduce((acc, s) => acc + s.controls.lines.length, 0);
  });

  /**
   * Lines still awaiting an answer. The certificate is a fiscal document signed with the officer's name, so
   * an undecided line must not be able to slip through as though it had been considered.
   */
  readonly undecidedLineCount = computed(() => {
    this.formChanges();
    return this.sources.controls.reduce((acc, s) => acc + s.controls.lines.controls.filter((l) => !l.controls.decision.value).length, 0);
  });

  readonly hasUndecidedLines = computed(() => this.undecidedLineCount() > 0);

  /** Lines actually staying on the certificate — everything not marked for removal. */
  readonly keptLineCount = computed(() => {
    this.formChanges();
    return this.sources.controls.reduce(
      (acc, s) => acc + s.controls.lines.controls.filter((l) => l.controls.decision.value !== 'remove').length,
      0,
    );
  });

  /**
   * Post is refused while any line is undecided, when nothing is left to withhold from, or when every line
   * was removed — the server refuses the last two as well.
   */
  readonly canPost = computed(
    () => this.isDraft() && this.isPersisted() && this.sourceCount() > 0 && this.keptLineCount() > 0 && !this.hasUndecidedLines(),
  );

  /** Saving a draft has the same requirement: the payload cannot express "no answer yet". */
  readonly canSaveDraft = computed(() => this.isDraft() && this.isPersisted() && !this.hasUndecidedLines());

  // ── save / post ─────────────────────────────────────────────────────────────
  private buildPayload(): WithholdingSaveCommand {
    const v = this.form.getRawValue();
    return {
      documentDate: v.documentDate ? v.documentDate.toISOString() : null,
      postingDate: v.postingDate ? v.postingDate.toISOString() : null,
      notes: v.notes ?? null,
      sources: this.sources.controls.map((source) => ({
        documentReference: source.controls.documentReference.value,
        // A Lempira document converts at 1; sending a rate for it would be meaningless.
        localRate: this.sourceIsLocal(source) ? null : source.controls.localRate.value,
        // The submitted set IS the coverage, so "remove" is expressed by OMITTING the line rather than by a
        // flag — which is exactly what the API's save contract already means by a missing line. Undecided
        // lines cannot reach here: canSave/canPost block the submit until every one has an answer.
        lines: source.controls.lines.controls
          .filter((line) => line.controls.decision.value !== 'remove')
          .map((line) => ({
            sourceLineNumber: line.controls.sourceLineNumber.value,
            isExempt: line.controls.decision.value === 'exempt',
          })),
      })),
    };
  }

  saveDraft(): void {
    const id = this.currentId();
    if (!id) return;
    if (this.hasUndecidedLines()) {
      GlobalMessageUtil.error(
        this.t('Error', 'Error'),
        this.t(
          'Withholding_UndecidedLines',
          'Every line needs a decision (Withhold, Exempt, or Remove) before this certificate can be saved.',
        ),
      );
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return;
    }
    this.startLoading();
    this.withholdingDocumentService
      .save(id, this.buildPayload())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            GlobalMessageUtil.success(this.t('Saved', 'Saved'), this.t('Withholding_Saved', 'Withholding certificate saved.'));
            this.patchFromCertificate(response.data);
          }
        },
      });
  }

  post(): void {
    const id = this.currentId();
    if (!id) return;
    this.confirmationService.confirm({
      header: this.t('Withholding_Post_Confirm_Header', 'Post Withholding Certificate'),
      message: this.t(
        'Withholding_Post_Confirm_Message',
        'Posting creates the withholding journal entry (Dr AP / Cr Retenciones por Pagar) and reduces each AP document to its net. Continue?',
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => this.doPost(id),
    });
  }

  private doPost(id: string): void {
    // Persist the current decisions first, then post — so the officer's latest picks are what post.
    this.startLoading();
    this.withholdingDocumentService.save(id, this.buildPayload()).subscribe({
      next: () =>
        this.withholdingDocumentService
          .post(id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: (response) => {
              if (response.data) {
                GlobalMessageUtil.success(this.t('Posted', 'Posted'), this.t('Withholding_Posted', 'Withholding certificate posted.'));
                this.patchFromCertificate(response.data);
              }
            },
          }),
      error: () => this.stopLoading(),
    });
  }

  back(): void {
    this.router.navigate(['/ap-withholdings']);
  }
}
